import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocation, Outlet, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Clock, 
  CheckCircle, 
  AlertTriangle, 
  FileText, 
  Shield, 
  UserCheck, 
  Upload,
  Users,
  User,
  CreditCard,
  ArrowRight,
  RefreshCw,
  Eye,
  Download,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Star,
  TrendingUp,
  BarChart3,
  Settings,
  Bell,
  LogOut,
  Plus,
  Edit,
  Trash2,
  AlertCircle,
  Info,
  DollarSign,
  FileCheck,
  UserPlus,
  Activity,
  Briefcase,
  Loader
} from 'lucide-react'
import toast from 'react-hot-toast'
import { vendorService } from '../../services/vendorService'
import VendorSidebar from '../../components/vendor/VendorSidebar'
import VendorHeader from '../../components/vendor/VendorHeader'
import { createContext, useContext } from 'react'
import Modal from '../../components/common/Modal'

// Local context for vendor dashboard state
const VendorDashboardContext = createContext()
export const useVendorDashboard = () => {
  const context = useContext(VendorDashboardContext)
  if (!context) {
    throw new Error('useVendorDashboard must be used within VendorDashboard')
  }
  return context
}

const VendorDashboard = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [vendorStatus, setVendorStatus] = useState('pending')
  const [uploadingFile, setUploadingFile] = useState(null)
  const [uploadedDocuments, setUploadedDocuments] = useState({})
  const [resources, setResources] = useState([])
  const [contactPersonProfile, setContactPersonProfile] = useState(null)
  // Loading state used while fetching dashboard/profile data
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingAccess, setIsCheckingAccess] = useState(true)
  
  // Rejection reasons
  const [bgvRejectionReason, setBgvRejectionReason] = useState('')
  const [adminRejectionReason, setAdminRejectionReason] = useState('')
  
  // Form data for skills and profile
  const [formData, setFormData] = useState({
    primarySkills: [],
    secondarySkills: [],
    technologies: [],
    allSkills: []
  })
  const [confirmModal, setConfirmModal] = useState(null)

  // Check if user has access to dashboard (admin review must be approved)
  useEffect(() => {
    const checkDashboardAccess = async () => {
      try {
        setIsCheckingAccess(true)
        const currentUser = await vendorService.getCurrentUser()
        
        if (!currentUser) {
          navigate('/auth/login')
          return
        }

        // Check admin review status
        const adminReview = currentUser.admin_review || currentUser.adminReview
        const userStatus = currentUser.status

        // Only allow dashboard access if admin review is approved and status is active
        if (adminReview !== 'approved' || userStatus !== 'active') {
          // Redirect to document upload/verification page
          navigate('/vendor/registration/documents', { replace: true })
          return
        }

        // User has access, continue loading dashboard
        setIsCheckingAccess(false)
      } catch (error) {
        console.error('Error checking dashboard access:', error)
        navigate('/vendor/registration/documents', { replace: true })
      }
    }

    checkDashboardAccess()
  }, [navigate])

  // Logout function
  const handleLogout = async () => {
    setConfirmModal({
      message: 'Are you sure you want to logout?',
      onConfirm: async () => {
        try {
          await vendorService.logout()
          toast.success('Logged out successfully')
          navigate('/') // Redirect to home page
        } catch (error) {
          toast.error('Error logging out')
        }
      }
    });
  }

  const handleNavigateToProfile = () => {
    navigate('/vendor/dashboard/profile')
  }

  // Handle file upload
  const handleFileUpload = async (documentName) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png'
    input.onchange = async (e) => {
      const file = e.target.files[0]
      if (file) {
        // Validate file format
        const allowedTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'image/jpeg',
          'image/jpg',
          'image/png'
        ]
        
        if (!allowedTypes.includes(file.type)) {
          toast.error(`Invalid file format. Please upload PDF, DOC, DOCX, JPG, or PNG files only.`)
          return
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`File size too large. Please upload files smaller than 10MB.`)
          return
        }

        setUploadingFile(documentName)
        
        try {
          // Get vendor_id from sessionStorage
          const vendorId = sessionStorage.getItem('vendorId')
          
          // Prepare document data for API
          const documentData = {
            file,
            document_type: documentName.replace(/\s+/g, '_').toLowerCase(), // e.g., 'certificate_of_incorporation'
            document_name: file.name,
            document_category: 'vendor_documents',
            user_role: 'vendor',
            vendor_id: vendorId,
            description: `Vendor document: ${documentName}`
          }
          
          // Call API to upload document
          await vendorService.uploadDocument(documentData)
          
          // After upload, always refresh dashboard data from getCurrentUser API
          // Use toast with id to prevent duplicates
          toast.success(`${documentName} uploaded successfully!`, { id: `upload-${documentName}` })
          await fetchVendorDashboard();
        } catch (error) {
          toast.error(error.message || 'Upload failed. Please try again.', { id: `upload-error-${documentName}` })
        } finally {
          setUploadingFile(null)
        }
      }
    }
    input.click()
  }

  // Handle document view
  const handleViewDocument = (document) => {
    if (document.url) {
      window.open(document.url, '_blank')
    } else {
      toast.error('Document URL not available')
    }
  }



  // Calculate document upload status
  const requiredDocuments = [
    'Certificate of Incorporation',
    'GST Registration Certificate', 
    'PAN Card Copy',
    'Audited Financial Statements (Last 2 Years)',
    'Signed NDA',
    'Master Service Agreement'
  ]
  
  const uploadedCount = requiredDocuments.filter(doc => 
    uploadedDocuments[doc] && 
    (uploadedDocuments[doc].status === 'APPROVED' || uploadedDocuments[doc].status === 'approved')
  ).length
  const allDocumentsUploaded = uploadedCount === requiredDocuments.length

  // Vendor data is sourced exclusively from the server via getCurrentUser() in fetchVendorDashboard()
  // (Do not read vendor registration data from sessionStorage.)

  // Fetch vendor dashboard data from API
  const fetchVendorDashboard = useCallback(async () => {
    try {
      setIsLoading(true)
      // Get current user data which includes BGV and admin review status
      const response = await vendorService.getCurrentUser()
      
      // Update all states with API response data
      if (response) {
        const apiData = response

        // Update vendor data with the API response structure
        setVendorData(prev => ({
          ...prev,
          companyName: apiData.name || prev.companyName,
          contactPerson: apiData.name || prev.contactPerson,
          email: apiData.email || prev.email,
          phone: prev.phone, // Phone not in this API response
          registrationDate: prev.registrationDate, // Registration date not in this API response
          subscriptionPlan: apiData.subscriptionPlan, // Subscription plan not in this API response
          status: apiData.status || prev.status,
          verification: apiData.status === 'active' ? 'completed' : 'pending',
          bgvStatus: apiData.BGV_status || null,
          adminReviewStatus: apiData.admin_review || null,
          progress: {
            documentUpload: { completed: apiData.BGV_status === 'completed' && apiData.all_documents_urls && Object.keys(apiData.all_documents_urls).length > 0, required: true },
            bgv: { completed: apiData.BGV_status === 'completed', required: true },
            adminReview: { completed: apiData.admin_review === 'approved', required: true },
            approval: { completed: apiData.status === 'active', required: true }
          },
        }))

        // Update verification statuses based on API data
        setVerificationStatuses({
          documents: apiData.BGV_status === 'completed' && apiData.all_documents_urls && Object.keys(apiData.all_documents_urls).length > 0 ? 'completed' : 'pending',
          bgv: apiData.BGV_status === 'completed' ? 'completed' :
               apiData.BGV_status === 'in-progress' ? 'in-progress' :
               apiData.BGV_status === 'pending' ? 'pending' : 'blocked',
          adminReview: apiData.admin_review === 'approved' ? 'completed' :
                     apiData.admin_review === 'in-review' ? 'in-review' :
                     apiData.admin_review === 'pending' ? 'pending' :
                     apiData.admin_review === 'rejected' ? 'blocked' : 'blocked',
          approval: apiData.status === 'active' ? 'completed' : 'blocked'
        })

        // Set rejection reasons
        setBgvRejectionReason(apiData.bgv_reason || '')
        setAdminRejectionReason(apiData.admin_review_reason || '')

        // Update uploaded documents from all_documents_urls and set vendorData.documents
        if (apiData.all_documents_urls) {
          const docsObject = {}
          const docsArray = []

          const normalizeDocKeys = (key) => {
            switch (key) {
              case 'certificate_of_incorporation': return ['Certificate of Incorporation']
              case 'gst_registration_certificate': return ['GST Registration Certificate']
              case 'pan_card_copy': return ['PAN Card Copy']
              case 'audited_financial_statements_(last_2_years)': return ['Audited Financial Statements (Last 2 Years)']
              case 'signed_nda': return ['Signed NDA']
              case 'master_service_agreement': return ['Master Service Agreement']
              default:
                // Fallback: prettify the key
                return [key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())]
            }
          }

          const extractFileName = (url) => {
            if (!url) return null
            try { return url.split('/').pop() } catch (e) { return null }
          }

          Object.entries(apiData.all_documents_urls).forEach(([rawKey, docData]) => {
            const displayNames = normalizeDocKeys(rawKey)
            const url = docData.file_url || docData.fileUrl || null
            const statusRaw = (docData.status || '').toString().toLowerCase()
            const status = statusRaw === 'pending' || statusRaw === 'p' ? 'pending' : statusRaw === 'approved' ? 'approved' : (statusRaw || 'uploaded')

            displayNames.forEach((displayName) => {
              // Avoid overwriting an existing specific entry (prefer specific API keys over combined)
              if (docsObject[displayName]) return

              const docObj = {
                name: displayName,
                fileName: docData.file_name || extractFileName(url) || displayName,
                type: docData.file_type || 'application/pdf',
                size: docData.size || 0,
                uploadDate: docData.uploadDate || new Date().toISOString(),
                status: status,
                url: url,
                documentId: docData.document_id || `doc_${Date.now()}_${Math.random()}`
              }

              // Key by displayName for uploadedDocuments
              docsObject[displayName] = docObj
              docsArray.push(docObj)
            })
          })

          setUploadedDocuments(docsObject)
          // Also keep vendorData.documents in sync for the UI that maps over vendorData.documents
          setVendorData(prev => ({ ...prev, documents: docsArray }))
        }

        // Update vendor status
        if (apiData.status) {
          setVendorStatus(apiData.status === 'active' ? 'active' : 'pending')
        }
      }
    } catch (error) {
      // Don't show error toast for dashboard data fetch to avoid annoying users
      // The component will use default values
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Fetch vendor dashboard data from API
  useEffect(() => {
    fetchVendorDashboard()
  }, [fetchVendorDashboard])

  // Get vendor data from API (initially empty, will be populated by API call)
  const [vendorData, setVendorData] = useState({
    companyName: 'Loading...', // Will be updated from API
    contactPerson: 'Loading...',
    email: 'Loading...',
    phone: 'Loading...',
    registrationDate: new Date().toISOString().split('T')[0],
    subscriptionPlan: 'Loading...',
    status: 'pending',
    verification: 'pending',
    progress: {
      documentUpload: { completed: false, required: true },
      bgv: { completed: false, required: true },
      adminReview: { completed: false, required: true },
      approval: { completed: false, required: true }
    },
    subscription: {
      plan: 'Professional',
      price: 2499,
      billingCycle: 'monthly',
      features: [
        'Advanced project matching',
        'Priority support',
        'Enhanced profile views',
        'Advanced analytics & reports',
        'Custom branding',
        'API access'
      ],
      limits: {
        projects: 25,
        profileViews: 500
      }
    },
    documents: [],
    payments: []
  })

  const sidebarItems = useMemo(() => [
    {
      id: 'overview',
      name: 'Overview',
      icon: <BarChart3 className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'bench-resources',
      name: 'Bench Resources',
      icon: <Briefcase className="w-5 h-5" />,
      badge: resources.length > 0 ? resources.length : null
    },
    {
      id: 'purchase-orders',
      name: 'Purchase Orders',
      icon: <FileText className="w-5 h-5" />,
      badge: null // Will show pending PO count
    },
    {
      id: 'interviews',
      name: 'Interviews',
      icon: <UserCheck className="w-5 h-5" />,
      badge: null // Will show scheduled interview count
    },
    {
      id: 'placements',
      name: 'Placements',
      icon: <CheckCircle className="w-5 h-5" />,
      badge: null // Will show active placements count
    },
    {
      id: 'subscription',
      name: 'Subscription',
      icon: <CreditCard className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'payments',
      name: 'Payments',
      icon: <DollarSign className="w-5 h-5" />,
      badge: null
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      badge: null
    }
  ], [resources.length])

  

  // Mock verification statuses - in real app, these would come from backend
  const [verificationStatuses, setVerificationStatuses] = useState({
    documents: 'pending', // Initially pending, only completed when BGV_status is 'completed'
    bgv: 'blocked', // BGV blocked until documents completed
    adminReview: 'blocked', // Admin review blocked until BGV completed
    approval: 'blocked' // Approval blocked until admin review completed
  })

  // Verification statuses are now derived from server state only
  // No more sessionStorage persistence - statuses come from getCurrentUser API

  const verificationSteps = [
    {
      id: 1,
      title: 'Document Upload & KYC',
      description: verificationStatuses.documents === 'completed' 
        ? 'All documents uploaded successfully' 
        : `Upload required documents for verification (${uploadedCount}/${requiredDocuments.length} uploaded)`,
      icon: <Upload className="w-6 h-6" />,
      status: verificationStatuses.documents,
      required: true,
      estimatedTime: '15 minutes'
    },
    {
      id: 2,
      title: 'Background Verification',
      description: verificationStatuses.bgv === 'completed' 
        ? 'Background verification cleared by third-party partner'
        : verificationStatuses.bgv === 'in-progress'
        ? 'Third-party partner is conducting background verification'
        : verificationStatuses.bgv === 'blocked' && bgvRejectionReason
        ? `Background verification failed: ${bgvRejectionReason}`
        : 'Automated verification checks via third-party APIs',
      icon: <Shield className="w-6 h-6" />,
      status: verificationStatuses.bgv,
      required: true,
      estimatedTime: '2-4 hours'
    },
    {
      id: 3,
      title: 'Admin Review',
      description: verificationStatuses.adminReview === 'completed'
        ? 'Admin review completed successfully'
        : verificationStatuses.adminReview === 'in-review'
        ? 'Compliance team is reviewing your documents'
        : verificationStatuses.adminReview === 'blocked' && adminRejectionReason
        ? `Admin review failed: ${adminRejectionReason}`
        : 'Manual review by compliance team',
      icon: <UserCheck className="w-6 h-6" />,
      status: verificationStatuses.adminReview,
      required: true,
      estimatedTime: '24-48 hours'
    },
    {
      id: 4,
      title: 'Approval & Activation',
      description: 'Account activation and feature unlock',
      icon: <CheckCircle className="w-6 h-6" />,
      status: verificationStatuses.approval,
      required: true,
      estimatedTime: 'Immediate'
    }
  ]

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-600" />
      case 'in-progress':
        return <RefreshCw className="w-5 h-5 text-primary-600 animate-spin" />
      case 'in-review':
        return <Eye className="w-5 h-5 text-yellow-600" />
      case 'pending':
        return <Clock className="w-5 h-5 text-gray-400" />
      case 'blocked':
        return <AlertTriangle className="w-5 h-5 text-gray-400" />
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-warning-600" />
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-error-600" />
      default:
        return <Clock className="w-5 h-5 text-gray-400" />
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success-100 dark:bg-success-900/30 border-success-200 dark:border-success-800'
      case 'in-progress':
        return 'bg-primary-100 dark:bg-primary-900/30 border-primary-200 dark:border-primary-800'
      case 'in-review':
        return 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800'
      case 'blocked':
        return 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700 opacity-60'
      case 'warning':
        return 'bg-warning-100 dark:bg-warning-900/30 border-warning-200 dark:border-warning-800'
      case 'error':
        return 'bg-error-100 dark:bg-error-900/30 border-error-200 dark:border-error-800'
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }
  }

  const handleStartVerification = (stepId) => {
    switch (stepId) {
      case 1:
        toast('Please complete document verification to proceed.', { icon: 'ℹ️' })
        break
      case 2:
        // BGV is typically triggered automatically after documents are uploaded
        // or by admin action. For now, just show info message.
        toast('Background verification will be initiated once all documents are uploaded and verified.', { icon: 'ℹ️' })
        break
      case 3:
        // Admin review is handled by admin users, not vendors
        toast('Admin review will be conducted by our compliance team after background verification is complete.', { icon: 'ℹ️' })
        break
      case 4:
        // Final approval is handled by admin
        toast('Final approval will be granted by our admin team after successful review.', { icon: 'ℹ️' })
        break
      default:
        break
    }
  }

  const handleWithdrawRegistration = () => {
    setConfirmModal({
      message: 'Are you sure you want to withdraw your registration? This action cannot be undone.',
      onConfirm: () => {
        toast.success('Registration withdrawal request submitted. You will be contacted within 24 hours.')
        // Handle withdrawal logic here
      }
    });
  }

  // Render functions moved to separate page components in src/pages/vendor/pages/

  // Context value to share with child components
  const contextValue = {
    vendorStatus,
    vendorData,
    resources,
    verificationStatuses,
    verificationSteps,
    bgvRejectionReason,
    adminRejectionReason,
    uploadedCount,
    requiredDocuments,
    uploadedDocuments,
    uploadingFile,
    isLoading,
    contactPersonProfile,
    fetchVendorDashboard,
    handleFileUpload,
    handleViewDocument,
    handleStartVerification,
    handleWithdrawRegistration,
    getStatusIcon,
    getStatusColor
  }

  // Show loading screen while checking access
  if (isCheckingAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 mx-auto mb-4 animate-spin text-primary-600" />
          <p className="text-gray-600 dark:text-gray-300">Verifying access...</p>
        </div>
      </div>
    )
  }

  return (
    <VendorDashboardContext.Provider value={contextValue}>
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 flex">
        {/* Sidebar */}
        <VendorSidebar
          sidebarOpen={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          sidebarItems={sidebarItems}
          onLogout={handleLogout}
        />

        {/* Main Content */}
        <div className={`flex-1 flex flex-col ${sidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300`}>
          {/* Header */}
          <VendorHeader
            sidebarItems={sidebarItems}
            companyName={vendorData.companyName}
            contactPersonProfile={contactPersonProfile}
            onProfileClick={handleNavigateToProfile}
          />

          {/* Content */}
          <div className="flex-1 p-4 md:p-6 overflow-y-auto">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <Loader className="w-12 h-12 mx-auto mb-4 animate-spin text-primary-600" />
                  <p className="text-gray-600 dark:text-gray-300">Loading dashboard data...</p>
                </div>
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </div>
      </div>

      {confirmModal && (
        <Modal
          isOpen={true}
          onClose={() => setConfirmModal(null)}
          title="Confirm Action"
        >
          <p className="text-gray-700 dark:text-gray-300 mb-4">{confirmModal.message}</p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setConfirmModal(null)}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={async () => {
                await confirmModal.onConfirm();
                setConfirmModal(null);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Confirm
            </button>
          </div>
        </Modal>
      )}
    </VendorDashboardContext.Provider>
  )
}

export default VendorDashboard